import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useTimerStore, useAppStore,
  onTimerState, saveTimerState, recordSessionOnce,
  getSessionDuration, getNextSessionType, formatTime, playAlarmSound,
  type CloudTimer, type SessionType, type Session,
} from '@flowfocus/core';
import { notify } from '../lib/notify';

/** Sessions that ended without connection wait here (localStorage) until they can be written. */
const PENDING_KEY = 'ff_pending_sessions';
const LATE_END_MS = 5 * 60 * 1000;
type PendingPwa = { session: Session; countForTask?: string };
const readPending = (): PendingPwa[] => { try { return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]'); } catch { return []; } };
const writePending = (l: PendingPwa[]) => { try { localStorage.setItem(PENDING_KEY, JSON.stringify(l)); } catch { /* storage full or blocked */ } };

async function saveSession(uid: string, p: PendingPwa): Promise<void> {
  const st = p.session.startedAt;
  await recordSessionOnce(uid, p.session, {
    tree: { id: `tree-${st}`, uid, sessionId: p.session.id, taskId: p.session.taskId, grownAt: p.session.endedAt ?? st, species: Math.floor((st / 1000) % 5), size: 3 },
    countForTask: p.countForTask,
  });
}

async function flushPending(uid: string) {
  for (const p of readPending()) {
    if (p.session.uid !== uid) continue;
    try { await saveSession(uid, p); } catch { return; }
    writePending(readPending().filter((x) => x.session.id !== p.session.id));
  }
}

/**
 * Cross-device timer. The authoritative state lives in Firestore
 * (users/{uid}/timer/state); every open device renders a live countdown from
 * `endsAt` and writes controls back. Session completion is recorded
 * idempotently (id derived from the session start) so two devices can't
 * double-count.
 */
export function useCloudTimer(uid?: string) {
  const settings = useTimerStore((s) => s.settings);
  const [snap, setSnap] = useState<CloudTimer | null>(null);
  const [, force] = useState(0);
  const ending = useRef(false);
  const snapRef = useRef<CloudTimer | null>(null);
  snapRef.current = snap;

  useEffect(() => {
    if (!uid) { setSnap(null); return; }
    return onTimerState(uid, setSnap);
  }, [uid]);

  // Retry sessions that ended offline: on open and whenever the connection comes back.
  useEffect(() => {
    if (!uid) return;
    void flushPending(uid);
    const on = () => void flushPending(uid);
    window.addEventListener('online', on);
    return () => window.removeEventListener('online', on);
  }, [uid]);

  useEffect(() => {
    if (snap?.status !== 'running') return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [snap?.status]);

  const write = useCallback((s: CloudTimer) => {
    const next = { ...s, updatedAt: Date.now() };
    setSnap(next);
    if (uid) void saveTimerState(uid, next);
  }, [uid]);

  const sessionType: SessionType = snap?.sessionType ?? 'work';
  const status = snap?.status ?? 'idle';
  const duration = snap?.duration ?? getSessionDuration(sessionType, settings);
  const remaining = snap
    ? status === 'running' && snap.endsAt
      ? Math.max(0, Math.round((snap.endsAt - Date.now()) / 1000))
      : snap.remaining
    : getSessionDuration(sessionType, settings);
  const progress = duration ? Math.min(1, (duration - remaining) / duration) : 0;

  // End detection (any open device).
  useEffect(() => {
    if (status === 'running' && remaining <= 0 && snap?.endsAt && !ending.current) {
      ending.current = true;
      void handleEnd();
    }
    if (status !== 'running') ending.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining, status]);

  async function handleEnd() {
    const s = snapRef.current;
    if (!s) return;
    const wasWork = s.sessionType === 'work';
    playAlarmSound(settings.alarmVolume);
    notify(
      wasWork ? '¡Sesión completada!' : '¡Descanso terminado!',
      wasWork ? 'Buen trabajo. Hora de un descanso.' : 'A enfocarse de nuevo.',
    );

    // The session ended at endsAt (the phone may have been asleep or the app closed until now)
    const endedAt = Math.min(s.endsAt ?? Date.now(), Date.now());
    const late = Date.now() - endedAt > LATE_END_MS;

    if (uid && wasWork) {
      const startedAt = s.sessionStartedAt ?? endedAt - s.duration * 1000;
      const tasks = useAppStore.getState().tasks;
      const p: PendingPwa = {
        session: {
          id: `s-${startedAt}`, uid,
          taskId: s.taskId,
          projectId: tasks.find((t) => t.id === s.taskId)?.projectId,
          type: 'work', startedAt, endedAt,
          durationSeconds: s.duration, completed: true,
        },
        countForTask: s.taskId,
      };
      // Without connection the transaction fails: the session waits in the local queue and the
      // timer moves on anyway (before it stayed stuck on 00:00 until reload).
      try { await saveSession(uid, p); }
      catch { writePending([...readPending().filter((x) => x.session.id !== p.session.id), p]); }
    }

    const next = getNextSessionType(s.sessionType, s.pomodorosThisSet, settings);
    const pom = wasWork ? (s.pomodorosThisSet + 1) % settings.longBreakAfter : s.pomodorosThisSet;
    const dur = getSessionDuration(next, settings);
    const autoStart = (wasWork ? settings.autoStartBreaks : settings.autoStartPomodoros) && !late;
    const now = Date.now();
    if (autoStart) {
      write({ status: 'running', sessionType: next, taskId: s.taskId, duration: dur, remaining: dur, endsAt: now + dur * 1000, pomodorosThisSet: pom, sessionStartedAt: next === 'work' ? now : undefined, updatedAt: now });
    } else {
      write({ status: 'idle', sessionType: next, taskId: s.taskId, duration: dur, remaining: dur, endsAt: undefined, pomodorosThisSet: pom, sessionStartedAt: undefined, updatedAt: now });
    }
  }

  const start = useCallback((taskId?: string) => {
    const s = snapRef.current;
    const type = s?.sessionType ?? 'work';
    const dur = getSessionDuration(type, settings);
    const now = Date.now();
    write({ status: 'running', sessionType: type, taskId: taskId !== undefined ? taskId : s?.taskId, duration: dur, remaining: dur, endsAt: now + dur * 1000, pomodorosThisSet: s?.pomodorosThisSet ?? 0, sessionStartedAt: type === 'work' ? now : undefined, updatedAt: now });
  }, [settings, write]);

  const pause = useCallback(() => {
    const s = snapRef.current;
    if (!s || s.status !== 'running' || settings.strictMode) return;
    const rem = s.endsAt ? Math.max(0, Math.round((s.endsAt - Date.now()) / 1000)) : s.remaining;
    write({ ...s, status: 'paused', remaining: rem, endsAt: undefined });
  }, [settings.strictMode, write]);

  const resume = useCallback(() => {
    const s = snapRef.current;
    if (!s || s.status !== 'paused') return;
    write({ ...s, status: 'running', endsAt: Date.now() + s.remaining * 1000 });
  }, [write]);

  const skip = useCallback(() => {
    const s = snapRef.current;
    if (!s || (settings.strictMode && s.status === 'running')) return;
    const wasWork = s.sessionType === 'work';
    const next = getNextSessionType(s.sessionType, s.pomodorosThisSet, settings);
    const pom = wasWork ? (s.pomodorosThisSet + 1) % settings.longBreakAfter : s.pomodorosThisSet;
    const dur = getSessionDuration(next, settings);
    write({ status: 'idle', sessionType: next, taskId: s.taskId, duration: dur, remaining: dur, endsAt: undefined, pomodorosThisSet: pom, sessionStartedAt: undefined, updatedAt: Date.now() });
  }, [settings, write]);

  const reset = useCallback(() => {
    const s = snapRef.current;
    if (!s || (settings.strictMode && s.status === 'running')) return;
    const dur = getSessionDuration(s.sessionType, settings);
    write({ ...s, status: 'idle', remaining: dur, duration: dur, endsAt: undefined, sessionStartedAt: undefined });
  }, [settings, write]);

  const setSession = useCallback((type: SessionType) => {
    const s = snapRef.current;
    const dur = getSessionDuration(type, settings);
    write({ status: 'idle', sessionType: type, taskId: s?.taskId, duration: dur, remaining: dur, endsAt: undefined, pomodorosThisSet: s?.pomodorosThisSet ?? 0, sessionStartedAt: undefined, updatedAt: Date.now() });
  }, [settings, write]);

  const setTask = useCallback((taskId?: string) => {
    const s = snapRef.current;
    const base: CloudTimer = s ?? {
      status: 'idle', sessionType: 'work',
      duration: getSessionDuration('work', settings), remaining: getSessionDuration('work', settings),
      pomodorosThisSet: 0, updatedAt: Date.now(),
    };
    write({ ...base, taskId });
  }, [settings, write]);

  return {
    snap, status, sessionType, taskId: snap?.taskId, pomodorosThisSet: snap?.pomodorosThisSet ?? 0,
    remaining, duration, progress, display: formatTime(remaining),
    start, pause, resume, skip, reset, setSession, setTask,
  };
}
